/**
 * UPLOAD-PREVIEW-1: one preview for a file in the bulk upload review.
 * Images and a HEIC preview_url render inline. A PDF uses the existing
 * /uploads url in a sandboxed iframe. docx, xlsx, csv, and txt load text
 * from the owner-scoped preview-text endpoint. Everything else shows the
 * name, size, and a Download link.
 */
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";

const IMAGE_TYPES = new Set([
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "image/heic", "image/heif", "image/tiff",
]);

export function previewKind(file) {
  const type = String(file?.file_type || "").split(";")[0].trim().toLowerCase();
  const name = String(file?.filename || "").toLowerCase();
  if (file?.preview_url || IMAGE_TYPES.has(type) || /\.(heic|heif|jpe?g|png|gif|webp|tiff?)$/.test(name)) {
    return "image";
  }
  if (type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (
    type.includes("wordprocessingml") || name.endsWith(".docx")
    || type.includes("spreadsheetml") || name.endsWith(".xlsx")
    || type === "text/csv" || name.endsWith(".csv")
    || type === "text/plain" || name.endsWith(".txt")
  ) {
    return "text";
  }
  return "other";
}

function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function UploadPreview({ file, projectId, sessionId, onClose }) {
  const kind = previewKind(file);
  const [text, setText] = useState("");
  const [failed, setFailed] = useState(false);
  const imageSrc = `${BACKEND_URL || ""}${file.preview_url || file.url || ""}`;
  const fileSrc = `${BACKEND_URL || ""}${file.url || ""}`;

  useEffect(() => {
    if (kind !== "text") return undefined;
    let cancelled = false;
    api.get(
      `/projects/${projectId}/bulk-upload/${sessionId}/files/${file.file_id}/preview-text`
    ).then((res) => {
      if (!cancelled) setText((res.data && res.data.text) || "");
    }).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => { cancelled = true; };
  }, [kind, projectId, sessionId, file.file_id]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      data-testid="upload-preview"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <p className="text-sm font-semibold text-slate-800 truncate">{file.filename}</p>
          <button type="button" onClick={onClose} aria-label="Close preview" className="p-1 text-slate-400">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {kind === "image" && (
            <img src={imageSrc} alt="" data-testid="image-preview" className="max-h-[60vh] mx-auto" />
          )}
          {kind === "pdf" && (
            <div className="space-y-3">
              <iframe
                title={file.filename || "PDF preview"}
                src={fileSrc}
                sandbox="allow-same-origin"
                data-testid="pdf-preview"
                className="w-full h-[60vh] border border-slate-200 rounded-lg"
              />
              <a
                href={fileSrc}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pdf-new-tab"
                className="text-sm text-blue-600"
              >
                Open in a new tab
              </a>
            </div>
          )}
          {kind === "text" && (
            failed
              ? <p data-testid="preview-text-error" className="text-sm text-slate-500">Preview text is unavailable.</p>
              : <pre data-testid="preview-text" className="whitespace-pre-wrap text-sm text-slate-700">{text}</pre>
          )}
          {kind === "other" && (
            <div data-testid="preview-download-panel" className="space-y-2">
              <p className="text-sm text-slate-800">{file.filename}</p>
              <p className="text-xs text-slate-500">{formatSize(file.size)}</p>
              <a href={fileSrc} data-testid="preview-download" className="text-sm text-blue-600">Download</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
